import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { store } from '../../app/store';
import {
  selectHasFetchedPatches,
  selectHasUnsavedChanges,
  selectPatches,
  selectPendingChanges,
  addPendingChange,
  removeOperationFromPending,
  clearPendingChanges,
  setHasFetchedPatches,
  setPatches
} from '../../features/patches/patchesSlice';
import { FranchiseGroup, PatchType, Store } from '../../types';
import { useSubmitPatches } from '../../features/patches/hooks/useSubmitPatches';
import { usePatchDataExtractor } from '../../features/patches/hooks/usePatchDataExtractor';
import { showToast } from '../../components/Toast/Toast';
import { ToastTypes } from '../../components/Toast/Toast.types';
import { genericErrorShowToastOptions } from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { MenuJobStatus, getInactiveMenuIds, refreshStoreCache } from '../../services/api';
import { usePublishMenu } from '../../services/extract/extract.service';
import { useMenuJobPolling } from '../../services/extract/publishJobPolling';
import { PublishMenusRequestBody } from '../../services/extract/extract.types';
import { useAllProducts } from '../../services/products/products.service';
import {
  getDealLoversProducts,
  groupDealLoversByCategory,
  buildSetVariantPriceOps,
  parseCents,
  getPriceStatus,
} from './dealLovers/dealLovers.utils';

// Helper function to convert patch type enum to display name
export const getPatchTypeDisplayName = (patchType: PatchType): string => {
  switch (patchType) {
    case PatchType.PRODUCT_PRICE:
      return 'Product Price';
    case PatchType.PRODUCT_EXCLUSION:
      return 'Product Exclusion';
    case PatchType.BUNDLE_PRICE:
      return 'Bundle Price';
    case PatchType.BUNDLE_EXCLUSION:
      return 'Bundle Exclusion';
    default:
      return patchType;
  }
};

export type NotificationType = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
};


export function useBulkUpdate() {
  const dispatch = useDispatch();

  // Redux state
  const hasFetchedPatches = useSelector(selectHasFetchedPatches);
  const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
  const patches = useSelector(selectPatches);
  const pendingChanges = useSelector(selectPendingChanges);

  // Add a new pending operation using the { minimal, enriched } wrapper
  type OperationWrapper = { minimal: any; enriched: any };
  const addPendingOp = (op: OperationWrapper, type: 'set' | 'remove' = 'set') => {
    const currentPatchIds = getPatchIdsForType(selectedPatchType);
    const newChange = {
      type: 'BulkUpdate',
      data: {
        patchIds: currentPatchIds,
        setOperations: type === 'set' ? [op] : [],
        removeOperations: type === 'remove' ? [op] : [],
      },
    };
    dispatch(addPendingChange(newChange));
  };

  // Remove a pending operation by index and type
  const removePendingOp = (index: number, opType: 'set' | 'remove') => {
    dispatch(removeOperationFromPending({ operationType: opType, operationIndex: index }));
  };

  // Clear all pending operations
  const clearPendingOps = () => {
    dispatch(clearPendingChanges());
  };

  // Use the submit patches hook
  const {
    submitPatches,
    resetStatus,
    isSubmitting,
    success,
    error: submitError,
    jobStatus,
    hasPendingChanges,
  } = useSubmitPatches();


  // Build the minimal backend payload from Redux pendingChanges
  const buildBackendPayload = useCallback(() => {
    const setOperations = (pendingChanges.data?.setOperations || []).map((op: OperationWrapper) => op.minimal);
    const removeOperations = (pendingChanges.data?.removeOperations || []).map((op: OperationWrapper) => op.minimal);
    const patchIds = pendingChanges.data?.patchIds || [];

    // Remove duplicate patch IDs
    const uniquePatchIds = Array.from(new Set(patchIds));

    // Build flattened payload to match updated PatchJobRequestDto (no operations container)
    const finalPayload: {
      patchIds: string[];
      setOperations?: any[];
      removeOperations?: any[];
    } = {
      patchIds: uniquePatchIds,
    };

    // Only include setOperations if there are any
    if (setOperations.length > 0) {
      finalPayload.setOperations = setOperations;
    }

    // Only include removeOperations if there are any
    if (removeOperations.length > 0) {
      finalPayload.removeOperations = removeOperations;
    }

    return finalPayload;
  }, [pendingChanges]);

  // Track pendingChanges updates for debugging
  useEffect(() => {
    // Show accumulated final payload whenever we have operations
    if (pendingChanges.data?.setOperations?.length > 0 || pendingChanges.data?.removeOperations?.length > 0) {
      buildBackendPayload(); // This will log the accumulated payload
    }
  }, [pendingChanges, buildBackendPayload, hasUnsavedChanges]);

  // Log and send backend payload when submitting
  const submitWithLogging = useCallback(() => {
    const payload = buildBackendPayload();
    // eslint-disable-next-line no-console
    console.debug('FINAL OPERATIONS PAYLOAD:', JSON.stringify(payload, null, 2));
    return submitPatches();
  }, [buildBackendPayload, submitPatches]);

  // Use the consolidated patch data extractor hook
  const { 
    patchData, 
    extractPatchDataFromStores, 
    getPatchDataForType,
    getPatchIdsForType, 
    clearPatchData 
  } = usePatchDataExtractor();


  // State
  const [activeStep, setActiveStep] = useState(0);
  const [bulkUpdateMode, setBulkUpdateMode] = useState<'manual' | 'bulk-upload'>('manual');
  const [selectedFranchises, setSelectedFranchises] = useState<FranchiseGroup[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [selectedPatchType, setSelectedPatchType] = useState<PatchType>(PatchType.PRODUCT_PRICE);
  const [pendingPatchType, setPendingPatchType] = useState<PatchType | null>(null);
  const [showOperationDrawer, setShowOperationDrawer] = useState(false);
  const [operationDrawerMode, setOperationDrawerMode] = useState<'create' | 'edit' | 'remove'>('create');
  const [notification, setNotification] = useState<NotificationType>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [selectedAction, setSelectedAction] = useState<'review' | 'update' | 'deal-lovers'>('review');
  // Deal Lovers: category name -> dollars string entered by the user
  const [dealLoversPrices, setDealLoversPrices] = useState<Record<string, string>>({});
  // Set when the deal-lovers flow has dispatched ops and is waiting to submit them
  const [dealLoversSubmitQueued, setDealLoversSubmitQueued] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSubmitToast, setShowSubmitToast] = useState(false);
  const [menuJobStatus, setMenuJobStatus] =
    useState<{
      jobId: string;
      status: string;
      progress: number;
      total: number;
      totalSubJobs: number;
      totalSubJobsCompleted: number;
      totalSubJobsFailed: number;
      totalSubJobsInProgress: number;
      startedAt?: string;
      completedAt?: string;
    } | null>(null);
  const [isMenuJobPolling, setIsMenuJobPolling] = useState(false);
  const [inactiveMenus, setInactiveMenus] = useState<any[]>([]);
  const [isLoadingInactiveMenus, setIsLoadingInactiveMenus] = useState(false);
  const [publishConfirmed, setPublishConfirmed] = useState(false);
  
  // Centralized error state for the flow
  const [error, setError] = useState<{ step?: number; message: string; type?: string } | null>(null);

  // Publish menu functionality
  const { mutateAsync: publishMenu } = usePublishMenu();
  const { pollJobStatus } = useMenuJobPolling();

  // Deal Lovers product data (only meaningfully used when selectedAction === 'deal-lovers')
  const { data: allProducts } = useAllProducts();

  const handleDealLoversPriceChange = useCallback((category: string, value: string) => {
    setDealLoversPrices((prev) => ({ ...prev, [category]: value }));
  }, []);

  /**
   * Build SetVariantPrice operations from the per-category prices: blank
   * categories are skipped; non-empty but non-whole-cents entries are reported
   * as invalid (and nothing is committed). For each valid category the price is
   * applied to every target variant of every Deal Lovers product in it,
   * accumulating into Redux (mirrors the manual "update" flow).
   * Returns { committed, invalidCategories }.
   */
  const commitDealLoversOps = useCallback(() => {
    const dealLoversProducts = getDealLoversProducts(allProducts || []);
    const categoryGroups = groupDealLoversByCategory(dealLoversProducts);

    // First pass: validate. Empty = skip, invalid = block.
    const invalidCategories: string[] = [];
    const pricedCategories: { category: string; amountCents: number }[] = [];

    categoryGroups.forEach((categoryGroup) => {
      const status = getPriceStatus(dealLoversPrices[categoryGroup.category] ?? '');
      if (status === 'empty') {
        return;
      }
      if (status === 'invalid') {
        invalidCategories.push(categoryGroup.category);
        return;
      }
      pricedCategories.push({
        category: categoryGroup.category,
        amountCents: parseCents(dealLoversPrices[categoryGroup.category] ?? '') as number,
      });
    });

    if (invalidCategories.length > 0) {
      return { committed: 0, invalidCategories };
    }

    // Second pass: commit. Build the variant ops for each priced category.
    dispatch(clearPendingChanges());
    const productsByCategory = new Map(categoryGroups.map((g) => [g.category, g.products]));
    const patchIds = Array.from(new Set(getPatchIdsForType(PatchType.PRODUCT_PRICE)));
    // A product can belong to multiple categories, so the same variant may be
    // reached more than once; dedupe by variantId (first priced category wins).
    const seenVariantIds = new Set<string>();
    let committed = 0;

    pricedCategories.forEach(({ category, amountCents }) => {
      (productsByCategory.get(category) || []).forEach((group) => {
        const ops = buildSetVariantPriceOps(group, amountCents);
        ops.forEach((op) => {
          const variantId = op.minimal.setVariantPrice.variantId;
          if (seenVariantIds.has(variantId)) {
            return;
          }
          seenVariantIds.add(variantId);
          dispatch(
            addPendingChange({
              type: 'BulkUpdate',
              data: {
                patchIds,
                // Cast at the dispatch boundary: the slice's Operation type requires
                // __typename, which only the `enriched` half carries (matches the
                // manual flow, where the wrapper is loosely typed).
                setOperations: [op as any],
                removeOperations: [],
              },
            })
          );
          committed += 1;
        });
      });
    });

    return { committed, invalidCategories };
  }, [allProducts, dealLoversPrices, dispatch, getPatchIdsForType]);

  const handleCloseSubmitToast = useCallback(() => {
    setShowSubmitToast(false);
  }, [resetStatus]);


  const handleBack = useCallback(() => {
    setActiveStep((prevStep) => prevStep - 1);
  }, []);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setSelectedFranchises([]);
    setSelectedStores([]);
    setSelectedPatchType(PatchType.PRODUCT_PRICE);
    setShowOperationDrawer(false);
    setDealLoversPrices({});
    setDealLoversSubmitQueued(false);
    dispatch(clearPendingChanges());
  }, [dispatch]);

  const handleFranchiseChange = useCallback((franchises: FranchiseGroup | FranchiseGroup[] | null, stores: Store[]) => {
    if (Array.isArray(franchises)) {
      setSelectedFranchises(franchises);
    } else if (franchises) {
      setSelectedFranchises([franchises]);
    } else {
      setSelectedFranchises([]);
    }

    // Update the selected stores based on the franchise selection
    setSelectedStores(stores);
    
    // Extract patch data when stores are selected
    if (stores.length > 0) {
      extractPatchDataFromStores(stores);
    } else {
      clearPatchData();
    }
  }, [extractPatchDataFromStores, clearPatchData]);

  const handleStoreSelection = useCallback((stores: Store | Store[] | null) => {
    let storeArray: Store[] = [];
    
    if (Array.isArray(stores)) {
      setSelectedStores(stores);
      storeArray = stores;
    } else if (stores) {
      setSelectedStores([stores]);
      storeArray = [stores];
    } else {
      setSelectedStores([]);
      storeArray = [];
    }
    
    // Extract patch data when stores are selected
    if (storeArray.length > 0) {
      extractPatchDataFromStores(storeArray);
    } else {
      clearPatchData();
    }
  }, [extractPatchDataFromStores, clearPatchData]);

  // Removed unused validateBeforePublish function

  // Validation before publishing
  const validateBeforePublish = useCallback(() => {
    // 1. Check for pending changes
    if (!hasPendingChanges) {
      setNotification({
        open: true,
        message: 'No changes to submit. Please create or edit operations first.',
        severity: 'warning',
      });
      return false;
    }

    // 2. Pricing validation (prices must be positive whole numbers representing cents)
    // Use enriched for UI validation
    const allOperations = [
      ...(pendingChanges?.data?.setOperations || []),
      ...(pendingChanges?.data?.removeOperations || [])
    ].map((op: OperationWrapper) => op.enriched);

    if (allOperations.length > 0) {
      const pricingErrors: string[] = [];

      allOperations.forEach((op: any, index: number) => {
        const opNum = index + 1;

        // Check different operation types for price validation
        if (op.__typename === 'SetVariantPriceMenuPatchOperation' && op.setVariantPrice?.authoringPrice?.amount !== undefined) {
          const price = op.setVariantPrice.authoringPrice.amount;
          const variantId = op.setVariantPrice.variant?.variantId || 'Unknown';
          if (typeof price !== 'number' || price < 0 || !Number.isInteger(price)) {
            pricingErrors.push(`Operation ${opNum} (Variant ${variantId}): Invalid price "${price}"`);
          }
        }

          if (op.__typename === 'SetBundlePriceMenuPatchOperation' && op.setBundlePrice?.authoringPrice?.amount !== undefined) {
            const price = op.setBundlePrice.authoringPrice.amount;
            // Use bundleCode for display, fallback to bundleId if missing
            const bundleCode = op.setBundlePrice.bundle?.bundleCode || op.setBundlePrice.bundle?.bundleId || 'Unknown';
            if (typeof price !== 'number' || price < 0 || !Number.isInteger(price)) {
              pricingErrors.push(`Operation ${opNum} (Bundle ${bundleCode}): Invalid price "${price}"`);
            }
          }

        if (op.__typename === 'SetModifierWeightPriceMenuPatchOperation' && op.setModifierWeightPrice?.authoringPrice?.amount !== undefined) {
          const price = op.setModifierWeightPrice.authoringPrice.amount;
          const variantId = op.setModifierWeightPrice.variant?.variantId || 'Unknown';
          const modifierId = op.setModifierWeightPrice.modifier?.modifierId || 'Unknown';
          if (typeof price !== 'number' || price < 0 || !Number.isInteger(price)) {
            pricingErrors.push(`Operation ${opNum} (Variant ${variantId}, Modifier ${modifierId}): Invalid price "${price}"`);
          }
        }

        if (op.__typename === 'SetModifierPriceRangesMenuPatchOperation' && op.setModifierPriceRanges?.weightPriceRanges) {
          const variantId = op.setModifierPriceRanges.variant?.variantId || 'Unknown';
          op.setModifierPriceRanges.weightPriceRanges.forEach((range: any, rangeIndex: number) => {
            if (range.authoringPrice?.amount !== undefined) {
              const price = range.authoringPrice.amount;
              if (typeof price !== 'number' || price < 0 || !Number.isInteger(price)) {
                pricingErrors.push(`Operation ${opNum} (Variant ${variantId}, Range ${rangeIndex + 1}): Invalid price "${price}"`);
              }
            }
          });
        }
      });

      if (pricingErrors.length > 0) {
        const errorMessage = pricingErrors.length === 1
          ? `Pricing error: ${pricingErrors[0]}. Prices must be positive whole numbers in cents (e.g., 400 = $4.00).`
          : `${pricingErrors.length} pricing errors found:\n• ${pricingErrors.slice(0, 3).join('\n• ')}${pricingErrors.length > 3 ? `\n• ...and ${pricingErrors.length - 3} more` : ''}\n\nPrices must be positive whole numbers in cents (e.g., 400 = $4.00).`;

        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        return false;
      }
    }

    return true;
  }, [hasPendingChanges, setNotification, pendingChanges]);

  // Step progression -- only handles patch submission, publish is decoupled
  const handleNext = useCallback(async () => {
    if (selectedAction === 'review' && activeStep >= 1) {
      return;
    }
    
    if (activeStep === 1 && selectedAction === 'deal-lovers') {
      const { committed, invalidCategories } = commitDealLoversOps();
      if (invalidCategories.length > 0) {
        setNotification({
          open: true,
          message: `Fix the price for ${invalidCategories.join(', ')} — enter a positive whole cents amount (e.g. 700) or clear the field to skip.`,
          severity: 'error',
        });
        return;
      }
      if (committed === 0) {
        setNotification({
          open: true,
          message: 'Enter a price for at least one category before publishing.',
          severity: 'warning',
        });
        return;
      }
      // Advance and queue submission. The freshly dispatched pending changes are
      // not visible to submitPatches' selector closure until a re-render, so a
      // useEffect (watching dealLoversSubmitQueued + hasPendingChanges) runs it.
      setActiveStep((prevStep) => prevStep + 1);
      setShowSubmitToast(true);
      setPublishConfirmed(false);
      setDealLoversSubmitQueued(true);
      return;
    }

    if (activeStep === 1 && selectedAction === 'update') {
      if (!validateBeforePublish()) {
        return;
      }
      if (hasPendingChanges) {
        setActiveStep((prevStep) => prevStep + 1);
        setShowSubmitToast(true);
        setPublishConfirmed(false);

        try {
          await submitPatches();
        } catch (patchError) {
          console.error('Error during patch submission:', patchError);
          setError({
            message: `Patch submission failed: ${patchError instanceof Error ? patchError.message : 'Unknown error'}`,
            type: 'submission',
            step: 2,
          });
          return;
        }
      } else {
        setNotification({
          open: true,
          message: 'No changes to submit. Please create or edit operations first.',
          severity: 'warning',
        });
      }
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  }, [activeStep, selectedAction, hasPendingChanges, submitPatches, validateBeforePublish, commitDealLoversOps]);

  // Submit deal-lovers operations once they are visible in Redux state.
  useEffect(() => {
    if (!dealLoversSubmitQueued || !hasPendingChanges) {
      return;
    }
    setDealLoversSubmitQueued(false);
    submitPatches().catch((patchError) => {
      console.error('Error during deal lovers patch submission:', patchError);
      setError({
        message: `Patch submission failed: ${patchError instanceof Error ? patchError.message : 'Unknown error'}`,
        type: 'submission',
        step: 2,
      });
    });
  }, [dealLoversSubmitQueued, hasPendingChanges, submitPatches]);

  // Publish & Verify -- triggered only after explicit user confirmation
  const handlePublishAndVerify = useCallback(async () => {
    const storeNumbers = selectedStores.map(store => store.storeNumber);
    const patchIds = pendingChanges?.data?.patchIds || [];
    let initialInactiveUUIDs: string[] = [];
    
    setPublishConfirmed(true);

    try {
      setIsLoadingInactiveMenus(true);
      const initialInactiveMenuIdsData = await getInactiveMenuIds(storeNumbers);
      initialInactiveUUIDs = (initialInactiveMenuIdsData?.inactiveMenuUUIDs || []).map((m: any) => m.inactiveMenuUUID);
    } catch (err) {
      console.error('Error getting initial inactive menu UUIDs:', err);
      setError({
        message: 'Error getting initial inactive menu UUIDs',
        type: 'network',
        step: 2,
      });
    } finally {
      setIsLoadingInactiveMenus(false);
    }

    const payload: PublishMenusRequestBody = {
      allStores: false,
      menuChannels: ['ECOMM'],
      stores: storeNumbers,
    };

    console.log('Publishing menus for patches:', patchIds, 'to stores:', payload.stores);

    try {
      setIsMenuJobPolling(true);
      const publishResult = await publishMenu(payload);

      if (publishResult[0]?.jobId) {
        const jobId = publishResult[0].jobId;

        setMenuJobStatus({
          jobId,
          status: MenuJobStatus.QUEUED,
          progress: 0,
          total: 0,
          totalSubJobs: 0,
          totalSubJobsCompleted: 0,
          totalSubJobsFailed: 0,
          totalSubJobsInProgress: 0,
          startedAt: undefined,
          completedAt: undefined
        });

        const jobStatus = await pollJobStatus(jobId);

        const storeCount = storeNumbers.length;
        const effectiveSubJobs = jobStatus.totalSubJobs > 0 ? jobStatus.totalSubJobs : storeCount;
        const effectiveCompleted = jobStatus.totalSubJobsCompleted > 0 ? jobStatus.totalSubJobsCompleted : 
          (jobStatus.status === MenuJobStatus.COMPLETED ? storeCount : 0);

        setMenuJobStatus({
          jobId: jobStatus.jobId,
          status: jobStatus.status,
          progress: effectiveCompleted,
          total: effectiveSubJobs,
          totalSubJobs: effectiveSubJobs,
          totalSubJobsCompleted: effectiveCompleted,
          totalSubJobsFailed: jobStatus.totalSubJobsFailed,
          totalSubJobsInProgress: jobStatus.totalSubJobsInProgress,
          startedAt: jobStatus.startedAt,
          completedAt: jobStatus.completedAt
        });

        if (jobStatus.status === MenuJobStatus.COMPLETED) {
          try {
            await refreshStoreCache(storeNumbers);
            await new Promise(resolve => setTimeout(resolve, 3000));
          } catch (err) {
            console.error('Error refreshing store cache:', err);
            setError({
              message: 'Error refreshing store cache',
              type: 'network',
              step: 3,
            });
            showToast({
              ...genericErrorShowToastOptions,
              label: 'Error refreshing store cache',
              toastType: ToastTypes.Error,
              id: 'errorRefreshingStoreCache',
            });
            setIsMenuJobPolling(false);
            return;
          }

          let attempts = 0;
          const maxAttempts = 60;
          const pollInterval = 5000;
          
          const pollForNewUUIDs = async (): Promise<boolean> => {
            try {
              attempts++;
              const newInactiveMenuIdsData = await getInactiveMenuIds(storeNumbers);
              const newUUIDs = (newInactiveMenuIdsData?.inactiveMenuUUIDs || []).map((m: any) => m.inactiveMenuUUID);
              
              const uuidsChanged = newUUIDs.length !== initialInactiveUUIDs.length || 
                !newUUIDs.every((uuid: string) => initialInactiveUUIDs.includes(uuid)) ||
                !initialInactiveUUIDs.every((uuid: string) => newUUIDs.includes(uuid));
              
              if (uuidsChanged) {
                await new Promise(resolve => setTimeout(resolve, 5000));
                setInactiveMenus(newUUIDs);
                return true;
              }
              
              if (attempts >= maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 5000));
                setInactiveMenus(newUUIDs);
                return true;
              }
              
              await new Promise(resolve => setTimeout(resolve, pollInterval));
              return pollForNewUUIDs();
              
            } catch (err) {
              console.error(`Error polling for inactive UUIDs (attempt ${attempts}):`, err);
              if (attempts >= maxAttempts) {
                setError({
                  message: 'Error verifying menu updates',
                  type: 'network', 
                  step: 3,
                });
                return false;
              }
              await new Promise(resolve => setTimeout(resolve, pollInterval));
              return pollForNewUUIDs();
            }
          };

          setIsLoadingInactiveMenus(true);
          const verificationSuccess = await pollForNewUUIDs();
          setIsLoadingInactiveMenus(false);

          if (verificationSuccess) {
            showToast({
              label: 'Menus published and verified successfully',
              toastType: ToastTypes.Success,
              id: 'successPublishingMenusInOrgs',
            });
          } else {
            showToast({
              label: 'Menus published but verification failed',
              toastType: ToastTypes.Info,
              id: 'warningMenuVerification',
            });
          }
        }
        
        setIsMenuJobPolling(false);
      }
    } catch (err) {
      setError({
        message: 'Error publishing menus',
        type: 'network',
        step: 2,
      });
      setIsMenuJobPolling(false);
      showToast({
        ...genericErrorShowToastOptions,
        label: 'Error publishing menus',
        toastType: ToastTypes.Error,
        id: 'errorPublishingMenus',
      });
    }
  }, [selectedStores, pendingChanges, publishMenu, pollJobStatus]);

  const handleBulkUpdateModeChange = useCallback((mode: 'manual' | 'bulk-upload') => {
    setBulkUpdateMode(mode);
    // Reset selections when switching modes
    if (mode === 'bulk-upload') {
      setSelectedStores([]);
    }
  }, []);

  const handlePatchTypeChange = useCallback(
    (patchType: PatchType) => {
      
      // First check with current selector values (for immediate response)
      const currentHasPendingOperations = pendingChanges.id && (
        pendingChanges.data.setOperations.length > 0 || 
        pendingChanges.data.removeOperations.length > 0
      );
      
      // If current state shows operations, show modal immediately
      if (patchType !== selectedPatchType && (hasUnsavedChanges || currentHasPendingOperations)) {
        setPendingPatchType(patchType);
        setShowConfirmationModal(true);
        return;
      }
      
      // If no current operations detected, double-check with latest store state
      // This handles React batching issues where Redux updates might be delayed
      setTimeout(() => {
        const currentState = store.getState();
        const latestPendingChanges = currentState.patches.pendingChanges;
        const latestHasUnsavedChanges = currentState.patches.hasUnsavedChanges;
        
        const hasPendingOperations = latestPendingChanges.id && (
          latestPendingChanges.data.setOperations.length > 0 || 
          latestPendingChanges.data.removeOperations.length > 0
        );
        
        if (patchType !== selectedPatchType && (latestHasUnsavedChanges || hasPendingOperations)) {
          setPendingPatchType(patchType);
          setShowConfirmationModal(true);
          return;
        }
        
        // Only proceed with patch type change if no operations found in either check
        if (selectedPatchType === patchType) return; // Already changed, skip
        
        setSelectedPatchType(patchType);
        dispatch(setHasFetchedPatches(false));
        dispatch(setPatches([]));
      }, 10); // Small delay to ensure Redux state has propagated
    },
    [selectedPatchType, hasUnsavedChanges, pendingChanges, dispatch, getPatchTypeDisplayName]
  );

  const handleActionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value as 'review' | 'update' | 'deal-lovers';
    setSelectedAction(newValue);
    // Reset to step 0 when changing action type to avoid invalid states
    setActiveStep(0);
    // Clear any in-progress deal-lovers entries when switching actions
    setDealLoversPrices({});
    setDealLoversSubmitQueued(false);
    dispatch(clearPendingChanges());
  }, [dispatch]);

  const handleSubmitOperationDrawer = useCallback(() => {
    setOperationDrawerMode('create');
    setShowOperationDrawer(true);
  }, []);

  const handleRemoveOperation = useCallback(() => {
    setOperationDrawerMode('remove');
    setShowOperationDrawer(true);
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleCloseConfirmationModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const handleConfirmationModalConfirm = useCallback(() => {
    setShowConfirmationModal(false);

    // If we have a pending patch type, use that instead of defaulting to PRODUCT_PRICE
    if (pendingPatchType) {
      setSelectedPatchType(pendingPatchType);
      setPendingPatchType(null);
    }

    dispatch(setHasFetchedPatches(false));
    dispatch(setPatches([]));
    dispatch(clearPendingChanges());
  }, [pendingPatchType, dispatch]);

  const closeOperationDrawer = useCallback(() => {
    setShowOperationDrawer(false);
  }, []);

  return {
    pendingChanges,
    addPendingOp,
    removePendingOp,
    clearPendingOps,
    submitWithLogging,
    // State
    activeStep,
    setActiveStep,
    bulkUpdateMode,
    selectedFranchises,
    selectedStores,
    selectedPatchType,
    setSelectedPatchType,
    pendingPatchType,
    showOperationDrawer,
    setShowOperationDrawer,
    operationDrawerMode,
    notification,
    selectedAction,
    setSelectedAction,
    dealLoversPrices,
    handleDealLoversPriceChange,
    showConfirmationModal,
    hasFetchedPatches,
    hasUnsavedChanges,
    showSubmitToast,
    success,
    submitError,
    isSubmitting,
    jobStatus,
    hasPendingChanges,
    menuJobStatus,
    isMenuJobPolling,
    inactiveMenus,
    isLoadingInactiveMenus,
    publishConfirmed,
    // Available patches from Redux
    availablePatches: patches,
    // Patch data management
    patchData,
    getPatchDataForType,
    getPatchIdsForType,
    // Centralized error state
    error,
    setError,
    // Handlers
    handleNext,
    handleBack,
    handleReset,
    handleBulkUpdateModeChange,
    handleFranchiseChange,
    handleStoreSelection,
    handlePatchTypeChange,
    handleActionChange,
    handleSubmitOperationDrawer,
    handleRemoveOperation,
    handleCloseNotification,
    handleCloseConfirmationModal,
    handleConfirmationModalConfirm,
    closeOperationDrawer,
    handleCloseSubmitToast,
    handlePublishAndVerify,
    submitPatches,
    getPatchTypeDisplayName,
  };

}